import type { CSSProperties } from "react";

/**
 * Per-branch tints for the calendar when several branches share it (Fer, 2026-11-03 11:29: "para el
 * Capulín y San José colores distintos … fechas distintas para pagar el agua y la luz").
 *
 * Nothing is stored and nothing names a laboratory: a branch takes the palette entry at its
 * position among the laboratory's branches ordered by id (ids are time-ordered, so a branch added
 * later never recolours the existing ones). Entries for every branch (`branch_id` null) stay
 * neutral. Red is left out on purpose: it means "overdue" on this screen, and so is the brand dot.
 * Each entry's text on its background is at least 4.5:1 (checked in `branchColors.test.ts`).
 */

export interface BranchTone {
  /** Soft background of a chip or card. */
  bg: string;
  /** Text on `bg`. */
  fg: string;
  /** Saturated edge and legend dot. */
  edge: string;
}

export const BRANCH_PALETTE: readonly BranchTone[] = [
  { bg: "#E1ECFA", fg: "#10325A", edge: "#2F6DB5" }, // blue
  { bg: "#DCF1EB", fg: "#0A453B", edge: "#1B8A78" }, // teal
  { bg: "#ECE4F8", fg: "#3A2070", edge: "#7A4FBF" }, // violet
  { bg: "#F3EBCF", fg: "#533D00", edge: "#A07600" }, // ochre
  { bg: "#E2EFD8", fg: "#274510", edge: "#5C9A2E" }, // green
  { bg: "#E3E8EE", fg: "#24303B", edge: "#5E6E7D" }, // slate
];

export type BranchToneOf = (branchId: string | null) => BranchTone | null;

/** The tone of a branch, or null for «todas las sucursales» / an unknown branch. */
export function branchToneOf(branchId: string | null, branches: readonly { id: string }[]): BranchTone | null {
  if (branchId === null) return null;
  const order = branches.map((b) => b.id).sort();
  const i = order.indexOf(branchId);
  return i < 0 ? null : BRANCH_PALETTE[i % BRANCH_PALETTE.length]!;
}

/** The lookup the calendar views use: tinted only while more than one branch is on screen. */
export function branchToneLookup(active: boolean, branches: readonly { id: string }[]): BranchToneOf {
  return (id) => (active && branches.length > 1 ? branchToneOf(id, branches) : null);
}

/** Inline CSS variables read by `.branch-tinted` (styles.css). */
export function toneStyle(tone: BranchTone | null): CSSProperties | undefined {
  return tone ? ({ "--bt-bg": tone.bg, "--bt-fg": tone.fg, "--bt-edge": tone.edge } as CSSProperties) : undefined;
}

/** WCAG contrast ratio of two #rrggbb colours. */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const [r, g, bl] = [1, 3, 5].map((i) => {
      const c = parseInt(hex.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r! + 0.7152 * g! + 0.0722 * bl!;
  };
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}
